import { Cents } from '@betterment/js-runtime';

import { BACKUP_WITHHOLDING_PERCENT } from '../constants';
import { BettermentAccount, InvestingAccount, LegalAccountType, RecurringTransferSubAccount } from '../data';

export const getId = (account?: BettermentAccount) => {
  if (account) {
    if (account.type === 'sub_account') return account.investingAccount.id;
    return account.checkingAccount.id;
  }
  return undefined;
};

export const isChecking = (account?: BettermentAccount) => account?.type === 'betterment_checking';

export const isIndividualCash = (account?: BettermentAccount) =>
  account?.type === 'sub_account' &&
  account.investingAccount.isCash &&
  account.investingAccount.legalAccount.type === 'individual_taxable';

export const isCash = (account?: BettermentAccount) =>
  account?.type === 'sub_account' && account.investingAccount.isCash;

export const isDirectTradingAccount = (account?: BettermentAccount) =>
  account?.type === 'sub_account' && account.investingAccount.isDirectTradingAccount;

export const isInvesting = (account?: BettermentAccount) =>
  account?.type === 'sub_account' && !account.investingAccount.isCash;

export const isTraditional401k = (account?: BettermentAccount) =>
  account?.type === 'sub_account' && account.investingAccount.legalAccount.type === 'traditional_401k';

export const isRoth401k = (account?: BettermentAccount) =>
  account?.type === 'sub_account' && account.investingAccount.legalAccount.type === 'roth_401k';

export const is401k = (account?: BettermentAccount) => isTraditional401k(account) || isRoth401k(account);

export const isHSA = (account?: BettermentAccount) =>
  account?.type === 'sub_account' && account.investingAccount.legalAccount.type === 'hsa';

export const is529 = (account?: BettermentAccount) =>
  account?.type === 'sub_account' && account.investingAccount.legalAccount.type === 'holding_529';

export const isTraditionalIRA = (account?: BettermentAccount) =>
  account?.type === 'sub_account' && account.investingAccount.legalAccount.type === 'traditional_ira';

export const isRothIRA = (account?: BettermentAccount) =>
  account?.type === 'sub_account' && account.investingAccount.legalAccount.type === 'roth_ira';

export const isSepIRA = (account?: BettermentAccount) =>
  account?.type === 'sub_account' && account.investingAccount.legalAccount.type === 'sep_ira';

export const isIRA = (account?: BettermentAccount) =>
  isTraditionalIRA(account) || isRothIRA(account) || isSepIRA(account);

export const isSolo401k = (account?: BettermentAccount) => {
  return account?.type === 'sub_account' && account.investingAccount.isSolo401K === true;
};

export const isSblocPledged = (account?: BettermentAccount) => {
  return account?.type === 'sub_account' && account.investingAccount.isSblocPledged;
};

export const recurringTransferSubAccountIsIRA = (account?: RecurringTransferSubAccount) =>
  account?.legalAccountType && ['traditional_ira', 'roth_ira', 'sep_ira'].includes(account.legalAccountType);

export const isLockedOrSuspended = (account?: BettermentAccount) =>
  account?.type === 'sub_account' && account.investingAccount.legalAccount.isLockedOrSuspended;

export const requiresIraContributionCappedWithdrawalAmount = (
  sourceAccount: BettermentAccount,
  destinationAccount: BettermentAccount,
  remainingContributionAmountCents: Cents
) =>
  !isCash(sourceAccount) &&
  isIRA(destinationAccount) &&
  sourceAccount.iraContributionCappedWithdrawalThresholdAmountCents &&
  sourceAccount.iraContributionCappedWithdrawalThresholdAmountCents >= remainingContributionAmountCents;

export const isRetirementIncome = (account?: BettermentAccount) =>
  account?.type === 'sub_account' && account.investingAccount.hasRetirementIncomePurpose;

export const isTaxable = (account?: BettermentAccount) =>
  account?.type === 'sub_account' &&
  ['individual_taxable', 'joint_taxable', 'trust_taxable'].includes(account.investingAccount.legalAccount.type);

export const requiresFullTransfer = (account: BettermentAccount, amountCents: Cents) => {
  if (isDirectTradingAccount(account)) {
    return false;
  }
  return amountCents >= account.fullWithdrawalThresholdAmountCents;
};

export const finalTransferAmountCents = (account: BettermentAccount, amountCents: Cents) =>
  requiresFullTransfer(account, amountCents) ? account.displayBalanceCents : amountCents;

export const backupWithholdingAmountCents = (amountCents: Cents) =>
  Math.floor(amountCents * BACKUP_WITHHOLDING_PERCENT * 0.01);

export const getAccountIdIfAvailable = (account?: BettermentAccount) => {
  if (account?.type === 'betterment_checking') {
    if (account.checkingAccount.locked || account.checkingAccount.available === false) {
      return undefined;
    }
    return getId(account);
  }
  return undefined;
};

export const getLegalAccountTypeDisplayName = (account?: BettermentAccount) =>
  account?.type === 'sub_account' ? account.investingAccount.legalAccount.typeDisplayName : undefined;

export const getLegalAccountType = (account?: BettermentAccount): LegalAccountType | undefined =>
  account?.type === 'sub_account' ? account.investingAccount.legalAccount.type : undefined;

export const allowsTaxImpact = (account?: BettermentAccount) =>
  !isCash(account) && !isDirectTradingAccount(account) && isTaxable(account);

export const backupWithholdingRequired = (sourceAccount: BettermentAccount | undefined) => {
  return sourceAccount === undefined
    ? false
    : isTaxable(sourceAccount) &&
        !isCash(sourceAccount) &&
        (sourceAccount as InvestingAccount).investingAccount.legalAccount.backupWithholdingActive;
};

export const hasDivorcedDfaOrCustomPortfolio = (account?: BettermentAccount) =>
  account?.type === 'sub_account' && !account.investingAccount.hasPortfolioEligibleForDeposit;

export const MAX_TRANSFER_AMOUNT_CENTS = 10_000_00;
